import React from 'react';

import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { IconButton, Tooltip } from '@mui/material';

import { onboardColors } from '../../theme/onboardTokens';

import type { IPortalItemRow } from '../types';

interface IDocumentActionsProps {
  row: IPortalItemRow;
  onPreview: (row: IPortalItemRow) => void;
  onPreviewUploadedAsset: (row: IPortalItemRow) => void;
  onDownloadLibraryDocument: (row: IPortalItemRow) => void;
  onDownloadUploadedAsset: (row: IPortalItemRow) => void;
  onDownloadForm: (row: IPortalItemRow) => void;
  onPreviewContract: () => void;
}

const ICON_SX = { color: onboardColors.neutral.icon, fontSize: 20 } as const;

/**
 * Renders the preview/download/upload actions for a submitted portal row.
 * Branches on row type (library document, uploaded document, smart form,
 * contract). Returns null when the row has no submitted-document actions.
 */
const DocumentActions = ({
  row,
  onPreview,
  onPreviewUploadedAsset,
  onDownloadLibraryDocument,
  onDownloadUploadedAsset,
  onDownloadForm,
  onPreviewContract
}: IDocumentActionsProps) => {
  if (row.status !== 'submitted') return null;

  if (row.type === 'library_document') {
    return (
      <div style={{ display: 'inline-flex', gap: 4 }}>
        <Tooltip title="Preview">
          <IconButton onClick={() => onPreview(row)} size="small">
            <VisibilityIcon sx={ICON_SX} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton onClick={() => onDownloadLibraryDocument(row)} size="small">
            <FileDownloadIcon sx={ICON_SX} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  if (row.type === 'document' && (row.asset_ids?.length ?? 0) > 0) {
    return (
      <div style={{ display: 'inline-flex', gap: 4 }}>
        <Tooltip title="Preview">
          <IconButton onClick={() => onPreviewUploadedAsset(row)} size="small">
            <VisibilityIcon sx={ICON_SX} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton onClick={() => onDownloadUploadedAsset(row)} size="small">
            <FileDownloadIcon sx={ICON_SX} />
          </IconButton>
        </Tooltip>
        {row.request_return ? (
          <Tooltip title="Upload">
            <IconButton onClick={() => onPreview(row)} size="small">
              <FileUploadIcon sx={ICON_SX} />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>
    );
  }

  if (row.type === 'document') {
    return null;
  }

  if (row.type === 'smart_form') {
    return (
      <div style={{ display: 'inline-flex', gap: 4 }}>
        <Tooltip title="Preview">
          <IconButton onClick={() => onPreview(row)} size="small">
            <VisibilityIcon sx={ICON_SX} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Download">
          <IconButton onClick={() => onDownloadForm(row)} size="small">
            <FileDownloadIcon sx={ICON_SX} />
          </IconButton>
        </Tooltip>
      </div>
    );
  }

  return (
    <Tooltip title="Preview">
      <IconButton
        onClick={() => (row.type === 'contract_signature' ? onPreviewContract() : onPreview(row))}
        size="small"
      >
        <VisibilityIcon sx={ICON_SX} />
      </IconButton>
    </Tooltip>
  );
};

export default DocumentActions;
